import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text, Button, Callout, Card, Spinner } from 'frosted-ui';
import { ArrowLeft16, ExclamationTriangle16, PlayCircle16 } from '@frosted-ui/icons';
import { api, PlanLimitError } from '../../services/api';
import { useCompanyId } from '../../contexts/CompanyContext';
import { useTerms } from '../../contexts/TermsContext';
import TermsAcceptanceModal from '../TermsAcceptanceModal';
import UpgradeDialog from '../billing/UpgradeDialog';
import ProblemCallout, { problemFrom, type Problem } from '../ProblemCallout';
import type { ExperienceAttachResult, Placement } from '../../types';
import { spotName, type TestPath } from '../../utils/paths';
import { describeOffer, variantLabel } from '../../utils/variants';
import { controlIsToday, type WizardData } from './wizardTypes';

interface StepGoLiveProps {
  experimentId: string;
  data: WizardData;
  path: TestPath;
  placements: Placement[];
  freeProductName?: string;
  attach?: ExperienceAttachResult | null; // in_whop: did the app get onto the free product?
  /** Unlock offers are new, so the control is the first price, not today's. */
  isUnlock?: boolean;
  /** Back to change the spots. */
  onBack: () => void;
  onLive: () => void;
}

export default function StepGoLive({
  experimentId,
  data,
  path,
  placements,
  freeProductName,
  attach,
  isUnlock = false,
  onBack,
  onLive,
}: StepGoLiveProps) {
  const navigate = useNavigate();
  const companyId = useCompanyId();
  const { isTermsAccepted } = useTerms();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<Problem | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  async function handleStart() {
    if (!isTermsAccepted) {
      setShowTermsModal(true);
      return;
    }
    await start();
  }

  async function handleTermsAccepted() {
    setShowTermsModal(false);
    await start();
  }

  // Links are only handed out once the test is live: a draft's link silently
  // falls back to the storefront and records nothing. No review prompt here:
  // the results page asks once a real visitor or sale arrives.
  async function start() {
    try {
      setStarting(true);
      setStartError(null);
      await api.startExperiment(experimentId);
    } catch (err) {
      // A draft from before the free test was used stays a draft until Pro.
      if (err instanceof PlanLimitError) {
        setShowUpgrade(true);
        return;
      }
      console.error('Failed to start test:', err);
      setStartError(problemFrom(err, 'Could not start the test. Try again.'));
      return;
    } finally {
      setStarting(false);
    }
    onLive();
  }

  const where =
    path === 'in_whop'
      ? [`Offer tab in ${freeProductName || 'your free product'}`]
      : placements.map(spotName);
  const rejected = placements.filter((p) => p.affiliateRejected);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          Ready to go live?
        </Heading>
        <Text size="2" color="gray">
          {path === 'in_whop'
            ? 'Free members see the offer as soon as you start. Until then, the tab says “Nothing here yet”.'
            : 'Your links appear as soon as you start. Until then, nobody is split between your prices and nothing is tracked.'}
        </Text>
      </div>

      <Card size="2">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {data.variants.map((v, i) => (
            <div
              key={v.name}
              className="pl-kv-row"
              style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}
            >
              <Text size="2" color="gray">
                {variantLabel(v.name)}
                {i === 0 ? (isUnlock ? ' (first price)' : controlIsToday(data) ? " (today's price)" : '') : ''}
              </Text>
              <Text size="2" weight="medium" className="vara2-tabular">
                {describeOffer({ ...v, currency: data.currency })} · {data.split[v.name] ?? 0}%
              </Text>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--gray-a5)', margin: 'var(--space-2) 0' }} />
          <Text size="2" color="gray">
            {where.length === 1 ? 'Spot: ' : 'Spots: '}
            <Text size="2" weight="medium" style={{ color: 'var(--gray-12)' }}>
              {where.join(', ')}
            </Text>
          </Text>
          {path === 'in_whop' && attach && (
            <Text size="2" color="gray">
              {attach.status === 'attached' || attach.status === 'found'
                ? `Paywall Lab is on ${freeProductName || 'your free product'} ✓`
                : `After you go live, add Paywall Lab to ${freeProductName || 'your free product'} — we'll show you how.`}
            </Text>
          )}
        </div>
      </Card>

      {rejected.length > 0 && (
        <Callout.Root color="yellow">
          <Callout.Icon>
            <ExclamationTriangle16 />
          </Callout.Icon>
          <Callout.Text>
            Whop didn't accept the username for {rejected.map((p) => p.label).join(', ')}, so that
            partner gets no credit for sales. Go back to fix it, or clear it.
          </Callout.Text>
        </Callout.Root>
      )}

      <div
        className="vara2-button-stack"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}
      >
        <Button
          variant="solid"
          color="green"
          size="3"
          onClick={handleStart}
          disabled={starting}
          style={{ flex: '1 1 220px' }}
        >
          {starting ? <Spinner size="1" /> : <PlayCircle16 />}
          Go live
        </Button>
        <Button
          variant="soft"
          color="gray"
          size="3"
          onClick={() => navigate(`/experiments/${experimentId}`)}
          style={{ flex: '1 1 160px' }}
        >
          Save as draft
        </Button>
      </div>
      {startError && <ProblemCallout problem={startError} />}

      <div>
        <Button type="button" variant="ghost" color="gray" size="2" style={{ minHeight: 44 }} onClick={onBack}>
          <ArrowLeft16 />
          Back
        </Button>
      </div>

      {showUpgrade && (
        <UpgradeDialog
          companyId={companyId}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={() => {
            setShowUpgrade(false);
            start();
          }}
        />
      )}

      {showTermsModal && (
        <TermsAcceptanceModal
          onCancel={() => setShowTermsModal(false)}
          onAccepted={handleTermsAccepted}
        />
      )}
    </div>
  );
}
